import { useEffect, useState } from "react";
import { toast } from "sonner";
import { SlidersHorizontal, CalendarPlus } from "lucide-react";
import { api, formatErr } from "../lib/api";
import { fmtDate } from "../lib/format";
import { Panel, Field, BTN } from "./Common";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";

const TypeSelect = ({ types, value, onChange, testid }) => (
  <Select value={value || undefined} onValueChange={onChange}>
    <SelectTrigger data-testid={testid}><SelectValue placeholder="Select leave type" /></SelectTrigger>
    <SelectContent>{types.map((t) => <SelectItem key={t.code} value={t.code}>{t.name}</SelectItem>)}</SelectContent>
  </Select>
);

const AdjustDialog = ({ open, onOpenChange, emp, types, onSaved }) => {
  const [f, setF] = useState({ leave_type: "", mode: "add", days: "", reason: "" });
  useEffect(() => {
    if (open) setF({ leave_type: "", mode: "add", days: "", reason: "" });
  }, [open]);
  const submit = async (e) => {
    e.preventDefault();
    const days = Number(f.days) * (f.mode === "add" ? 1 : -1);
    try {
      await api.post("/leave-adjustments", { user_id: emp.id, leave_type: f.leave_type, days, reason: f.reason });
      toast.success(`${Math.abs(days)} day(s) ${f.mode === "add" ? "added to" : "deducted from"} ${emp.name}'s balance`);
      onSaved();
      onOpenChange(false);
    } catch (err) { toast.error(formatErr(err)); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}><DialogContent className="bg-white" data-testid="adjust-balance-dialog">
      <DialogHeader><DialogTitle>Adjust balance · {emp?.name}</DialogTitle></DialogHeader>
      <form onSubmit={submit} className="space-y-3">
        <Field label="Leave type"><TypeSelect types={types.filter((t) => !t.unlimited)} value={f.leave_type} onChange={(v) => setF({ ...f, leave_type: v })} testid="adjust-type-select" /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Action">
            <Select value={f.mode} onValueChange={(v) => setF({ ...f, mode: v })}>
              <SelectTrigger data-testid="adjust-mode-select"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="add">Add days</SelectItem><SelectItem value="deduct">Deduct days</SelectItem></SelectContent>
            </Select>
          </Field>
          <Field label="Days"><Input type="number" min="0.5" step="0.5" required value={f.days} onChange={(e) => setF({ ...f, days: e.target.value })} data-testid="adjust-days-input" /></Field>
        </div>
        <Field label="Reason (saved in history)"><Input required value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} data-testid="adjust-reason-input" /></Field>
        <Button type="submit" disabled={!f.leave_type} className={`w-full ${BTN}`} data-testid="adjust-submit-button">Save adjustment</Button>
      </form>
    </DialogContent></Dialog>
  );
};

const RecordDialog = ({ open, onOpenChange, emp, types, onSaved }) => {
  const [f, setF] = useState({ leave_type: "", start_date: "", end_date: "", reason: "" });
  useEffect(() => {
    if (open) setF({ leave_type: "", start_date: "", end_date: "", reason: "" });
  }, [open]);
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/leaves/record", { ...f, user_id: emp.id });
      toast.success(`Leave recorded for ${emp.name}`);
      onSaved();
      onOpenChange(false);
    } catch (err) { toast.error(formatErr(err)); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}><DialogContent className="bg-white" data-testid="record-leave-dialog">
      <DialogHeader><DialogTitle>Record leave · {emp?.name}</DialogTitle></DialogHeader>
      <p className="text-xs text-[#6E6E73] -mt-2">Recorded leave is approved immediately and deducted from the balance.</p>
      <form onSubmit={submit} className="space-y-3">
        <Field label="Leave type"><TypeSelect types={types} value={f.leave_type} onChange={(v) => setF({ ...f, leave_type: v })} testid="record-type-select" /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="From"><Input type="date" required value={f.start_date} onChange={(e) => setF({ ...f, start_date: e.target.value })} data-testid="record-start-input" /></Field>
          <Field label="To"><Input type="date" required value={f.end_date} onChange={(e) => setF({ ...f, end_date: e.target.value })} data-testid="record-end-input" /></Field>
        </div>
        <Field label="Reason"><Input value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} data-testid="record-reason-input" /></Field>
        <Button type="submit" disabled={!f.leave_type} className={`w-full ${BTN}`} data-testid="record-submit-button">Record leave</Button>
      </form>
    </DialogContent></Dialog>
  );
};

const COLS = [["credited", "Credited"], ["carried", "Carried"], ["adjusted", "Adjusted"], ["used", "Used"], ["lapsed", "Lapsed"], ["pending", "Pending"], ["available", "Available"]];

export const LeaveHrTools = ({ onChange }) => {
  const [emps, setEmps] = useState([]);
  const [types, setTypes] = useState([]);
  const [empId, setEmpId] = useState("");
  const [bal, setBal] = useState(null);
  const [adjs, setAdjs] = useState([]);
  const [dlg, setDlg] = useState(null);
  const emp = emps.find((e) => e.id === empId);

  useEffect(() => {
    api.get("/employees").then((r) => setEmps(r.data.filter((e) => e.status === "active")));
    api.get("/leave-types").then((r) => setTypes(r.data.filter((t) => t.active)));
  }, []);
  const load = () => {
    if (!empId) return;
    api.get("/leaves/balance", { params: { user_id: empId } }).then((r) => setBal(r.data));
    api.get("/leave-adjustments", { params: { user_id: empId } }).then((r) => setAdjs(r.data));
  };
  useEffect(() => {
    load();
  }, [empId]); // eslint-disable-line react-hooks/exhaustive-deps
  const saved = () => { load(); onChange?.(); };

  return (
    <Panel title="HR leave tools" className="mb-6" testid="hr-leave-tools"
      actions={emp && <div className="flex gap-2">
        <Button size="sm" variant="outline" onClick={() => setDlg("adjust")} data-testid="open-adjust-balance-button"><SlidersHorizontal className="h-4 w-4 mr-1" />Adjust balance</Button>
        <Button size="sm" variant="outline" onClick={() => setDlg("record")} data-testid="open-record-leave-button"><CalendarPlus className="h-4 w-4 mr-1" />Record leave</Button>
      </div>}>
      <Select value={empId || undefined} onValueChange={setEmpId}>
        <SelectTrigger className="sm:w-80" data-testid="hr-tools-employee-select"><SelectValue placeholder="Select an employee to view or change their leave" /></SelectTrigger>
        <SelectContent>{emps.map((e) => <SelectItem key={e.id} value={e.id}>{e.name} ({e.employee_code})</SelectItem>)}</SelectContent>
      </Select>
      {bal && emp && (
        <div className="overflow-x-auto mt-4">
          <Table data-testid="hr-balance-table">
            <TableHeader><TableRow><TableHead>Leave type</TableHead>{COLS.map(([k, l]) => <TableHead key={k}>{l}</TableHead>)}</TableRow></TableHeader>
            <TableBody>{Object.entries(bal).map(([k, v]) => (
              <TableRow key={k} data-testid={`hr-balance-row-${k}`}>
                <TableCell className="font-semibold">{v.name}</TableCell>
                {COLS.map(([c]) => <TableCell key={c} className={c === "available" ? "font-bold" : ""}>{v.unlimited && !["used", "pending"].includes(c) ? "—" : v[c]}</TableCell>)}
              </TableRow>
            ))}</TableBody>
          </Table>
          {adjs.length > 0 && (
            <div className="mt-4">
              <div className="text-xs font-bold uppercase tracking-wider text-[#6E6E73] mb-2">Adjustment history</div>
              <div className="divide-y divide-[#F0F0F2] text-sm" data-testid="adjustment-history">
                {adjs.map((a) => (
                  <div key={a.id} className="py-2 flex justify-between gap-3">
                    <span>{a.leave_type_name}: <b className={a.days > 0 ? "text-emerald-700" : "text-red-600"}>{a.days > 0 ? `+${a.days}` : a.days}</b> · {a.reason}</span>
                    <span className="text-xs text-[#6E6E73] whitespace-nowrap">{fmtDate(a.effective_date)} · {a.by}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
      <AdjustDialog open={dlg === "adjust"} onOpenChange={(o) => !o && setDlg(null)} emp={emp} types={types} onSaved={saved} />
      <RecordDialog open={dlg === "record"} onOpenChange={(o) => !o && setDlg(null)} emp={emp} types={types} onSaved={saved} />
    </Panel>
  );
};
